/*
 * Copyright (c) Hilo 2020.
 * Author: fharold
 *
 * hilo-oauth-manager
 * HomePage.tsx
 */

import {PolicyListener} from "../../services/PolicyService";
import {RouteComponentProps} from 'react-router-dom';
import Policy from "../../tools/provider/Policy";
import HiloReactComponent from "../../tools/view/layout/HiloReactComponent";
import React from "react";
import PageTitle, {TitleTab} from "../../tools/view/layout/PageTitle";
import UIToast from "../../tools/view/toast/UIToast";
import AccessTokenService, {TokenSubjectType} from "../../services/AccessTokenService";
import AccessTokenDataProvider from "../../provider/AccessTokenDataProvider";
import List from "../../tools/list/List";
import AccessTokenAdapter from "../adapter/AccessTokenAdapter";
import HTTPService from "../../tools/service/HTTPService";
import {ListWatcher} from "../../tools/list/ListAdapter";
import Colors from "../../tools/model/enumerations/Colors";
import UIError from "../../tools/view/toast/UIError";
import User from "../../models/User";
import UserService from "../../services/UserService";
import Spinner from "../../tools/view/element/Spinner";
import AdminUserDetailsEditor from "../view/user/AdminUserDetailsEditor";
import AdminUserClientEditor from "../view/user/AdminUserClientEditor";
import AdminUserRoleEditor from "../view/user/AdminUserRoleEditor";
import Translation from "../../services/translation/Translation";
import PageComponent from "../../tools/view/layout/PageComponent";

interface MatchParams {
    id: string;
}

interface State {
    policy: Policy;
    pageIndex: number,
    user?: User;
    tokenDataProvider: AccessTokenDataProvider;
    tokenAdapter: AccessTokenAdapter;
}

export default class UserPage extends HiloReactComponent<RouteComponentProps<MatchParams>, State> implements PolicyListener, ListWatcher {

    private readonly _tokenService: AccessTokenService;
    private readonly _userService: UserService;
    private readonly _httpService: HTTPService;
    private _modalContainer?: Element;

    /**
     * constructor
     * @param props
     * @param state
     */
    constructor(props: RouteComponentProps<MatchParams>, state: State) {
        super(props, state);

        this._tokenService = this._serviceRepository.resolve(AccessTokenService.id) as AccessTokenService;
        this._userService = this._serviceRepository.resolve(UserService.id) as UserService;
        this._httpService = this._serviceRepository.resolve(HTTPService.id) as HTTPService;

        const provider = new AccessTokenDataProvider(this._tokenService, this._httpService, TokenSubjectType.USER, this.props.match.params.id);

        this.state = {
            policy: this._policyService.policy,
            pageIndex: 0,
            user: undefined,
            tokenDataProvider: provider,
            tokenAdapter: new AccessTokenAdapter(this.t, provider)
        };

        this._refreshUser();
    }

    /**
     * onUpdate
     */
    public onUpdate(): void {
        this.forceUpdate();
    }

    /**
     * componentDidMount
     */
    public componentDidMount(): void {
        super.componentDidMount();
        this._policyService.addListener(this);

        this._modalContainer = document.querySelector('#modal-container') || undefined;

        this.state.tokenDataProvider.subscribe(this);

        this._refreshUser();
    }

    /**
     * _refreshUser
     */
    private _refreshUser = async () => {
        let user = await this._userService.findById(this.props.match.params.id);
        await this.setState({user: user})

        this.state.tokenDataProvider.tokenSubjectId = user.id;
    };

    /**
     * componentWillUnmount
     */
    public componentWillUnmount() {
        this._policyService.removeListener(this);
        super.componentWillUnmount();
    }

    /**
     * componentDidUpdate
     * @param prevProps
     * @param prevState
     * @param snapshot
     */
    public componentDidUpdate(prevProps: Readonly<RouteComponentProps<MatchParams>>, prevState: Readonly<State>, snapshot?: any): void {
        if (prevProps.match.params.id !== this.props.match.params.id) {
            this.state.tokenDataProvider.tokenSubjectId = this.props.match.params.id;
            this._refreshUser();
        }
    }

    /**
     * policyUpdated
     * @param policy
     */
    public policyUpdated = (policy: Policy) => {
        this.setState({
            policy: policy
        })
    };

    /**
     * tokenList
     * @returns {any}
     * @private
     */
    private _tokenList = () => {
        const {tokenDataProvider, tokenAdapter} = this.state;

        return <List
            adapter={tokenAdapter}
            dataProvider={tokenDataProvider}
            accessoryButton={{
                labelAccessor: (t: Translation) => t.revoke,
                color: Colors.red,
                onClick: this._revokeTokens
            }
            }/>
    };

    /**
     * _revokeTokens
     * @private
     */
    private _revokeTokens = () => {
        if (!this.state.user) return;

        try {
            this._tokenService.revokeByUser(this.state.user);
            this.state.tokenDataProvider.load();
            UIToast.default.show(this.t.revoked);
        } catch (e) {
            UIError.default.show("Failed to revoke tokens.");
        }
    }

    /**
     * render
     */
    public render(): any {
        const {user} = this.state;

        if (!user) return <Spinner large/>

        const titleTabs: TitleTab[] = [
            {
                title: t => t.details,
                content: <AdminUserDetailsEditor user={user} userUpdated={this._refreshUser}/>,
            },
            {
                title: t => t.clients,
                content: <AdminUserClientEditor user={user} userUpdated={this._refreshUser}/>
            },
            {
                title: t => t.roles,
                content: <AdminUserRoleEditor user={user} userUpdated={this._refreshUser}/>
            },
            {
                title: t => t.token,
                content: <this._tokenList/>
            }
        ];

        return <PageComponent>
                <PageTitle title={t => t.usersAndAccess}
                           tabs={titleTabs}
                           onTabSelected={index => this.setState({pageIndex: index})}
                           subTitle={user?.email || this.t.loading}
                           backLabel={t => t.allUsers}
                />
                {titleTabs[this.state.pageIndex].content}
        </PageComponent>
    }
}